import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Medal,
  Eye,
  EyeOff,
  Search,
  Loader2,
  Volume2,
  Upload,
  Wand2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import AudioRecorder from './AudioRecorder';
import { ImagePicker } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Badge categories
const BADGE_CATEGORIES = [
  { value: 'vocabulary', label: 'Vocabulary' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'streak', label: 'Streak' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'technique', label: 'Technique' },
  { value: 'tournament', label: 'Tournament' },
];

// Badge rarity levels
const BADGE_RARITIES = [
  { value: 'common', label: 'Common', color: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600' },
  { value: 'uncommon', label: 'Uncommon', color: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700' },
  { value: 'rare', label: 'Rare', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700' },
  { value: 'epic', label: 'Epic', color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border-purple-300 dark:border-purple-700' },
  { value: 'legendary', label: 'Legendary', color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border-yellow-400 dark:border-yellow-600' },
];

// Criteria types for badge unlock
// Standard criteria - tracked with threshold values
const CRITERIA_TYPES = [
  // Standard criteria (threshold-based)
  { value: 'vocab_mastered', label: 'Vocabulary Mastered', description: 'Number of vocab terms marked as "Know It"' },
  { value: 'quizzes_passed', label: 'Quizzes Passed', description: 'Number of quizzes passed' },
  { value: 'perfect_quizzes', label: 'Perfect Quizzes', description: 'Quizzes with 100% score' },
  { value: 'vocab_streak', label: 'Vocab Answer Streak', description: 'Correct vocab answers in succession' },
  { value: 'streak_days', label: 'Activity Streak Days', description: 'Consecutive days earning points' },
  { value: 'total_points', label: 'Total Points', description: 'Lifetime points earned' },
  { value: 'techniques_mastered', label: 'Techniques Mastered', description: 'Verified technique count' },
  { value: 'attendance_count', label: 'Attendance Count', description: 'Classes attended' },
  { value: 'tournament_wins', label: 'Tournament Wins', description: 'First place finishes' },
  // Special criteria (for secret badges - use threshold=1 for boolean checks)
  { value: 'quizzes_in_day', label: 'Quizzes in One Day', description: 'Complete X quizzes in a single day', isSpecial: true },
  { value: 'all_quizzes_perfect', label: 'All Quizzes Perfect', description: 'Pass every accessible quiz with 100%', isSpecial: true },
  { value: 'specific_quiz_complete', label: 'Complete Specific Quiz', description: 'Complete a specific quiz', isSpecial: true, requiresQuizId: true },
  { value: 'specific_quiz_perfect', label: 'Perfect Specific Quiz', description: 'Pass a specific quiz with 100%', isSpecial: true, requiresQuizId: true },
  { value: 'fourth_tier_all', label: 'Fourth Tier All Categories', description: 'Reach 4th tier badge in all categories', isSpecial: true },
  { value: 'first_all_quizzes', label: 'First to Complete All Quizzes', description: 'Be the first student to complete all quizzes', isSpecial: true },
  { value: 'early_morning_practice', label: 'Early Morning Practice', description: 'Practice vocabulary before 7am', isSpecial: true },
  { value: 'late_night_practice', label: 'Late Night Practice', description: 'Practice vocabulary after 10pm', isSpecial: true },
];

// Common emojis for badges
const BADGE_EMOJIS = ['🏆', '🥇', '🥈', '🥉', '⭐', '🌟', '💫', '🔥', '💪', '🥋', '📚', '📖', '✅', '🎯', '🎖️', '🏅', '👑', '💎', '🚀', '⚡'];

// TTS Voice options
const TTS_VOICES = [
  { name: 'ja-JP-Neural2-D', gender: 'Male', type: 'Neural2' },
  { name: 'ja-JP-Neural2-B', gender: 'Female', type: 'Neural2' },
  { name: 'ja-JP-Wavenet-C', gender: 'Male', type: 'WaveNet' },
  { name: 'ja-JP-Wavenet-A', gender: 'Female', type: 'WaveNet' },
];

// ============================================
// Criteria Builder
// ============================================
const CriteriaBuilder = ({ criteria, onChange, quizzes = [] }) => {
  const addCriteria = () => {
    onChange([...criteria, { type: 'vocab_mastered', threshold: 10, timeframe_days: null, quiz_id: null }]);
  };

  const updateCriteria = (index, field, value) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: value };
    // Clear quiz_id if switching to a type that doesn't need it
    if (field === 'type') {
      const criteriaType = CRITERIA_TYPES.find(t => t.value === value);
      if (!criteriaType?.requiresQuizId) {
        updated[index].quiz_id = null;
      }
    }
    onChange(updated);
  };

  const removeCriteria = (index) => {
    onChange(criteria.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label>Unlock Criteria</Label>
      {criteria.map((c, index) => {
        const criteriaType = CRITERIA_TYPES.find(t => t.value === c.type);
        const needsQuiz = criteriaType?.requiresQuizId;
        const isSpecial = criteriaType?.isSpecial;

        return (
          <div key={index} className={`p-3 rounded-lg ${isSpecial ? 'bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800' : 'bg-gray-50 dark:bg-gray-800'}`}>
            <div className="flex items-center gap-2">
              <select
                value={c.type}
                onChange={(e) => updateCriteria(index, 'type', e.target.value)}
                className="flex-1 p-2 border rounded"
              >
                <optgroup label="Standard Criteria">
                  {CRITERIA_TYPES.filter(t => !t.isSpecial).map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Special Criteria (Secret Badges)">
                  {CRITERIA_TYPES.filter(t => t.isSpecial).map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              <Input
                type="number"
                min="1"
                value={c.threshold}
                onChange={(e) => updateCriteria(index, 'threshold', parseInt(e.target.value) || 1)}
                className="w-24"
                placeholder="Count"
                title={isSpecial ? "Threshold (use 1 for boolean checks)" : "Required count"}
              />
              <Input
                type="number"
                min="0"
                value={c.timeframe_days || ''}
                onChange={(e) => updateCriteria(index, 'timeframe_days', e.target.value ? parseInt(e.target.value) : null)}
                className="w-24"
                placeholder="Days"
                title="Timeframe in days (optional)"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeCriteria(index)}
              >
                <X className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            {/* Quiz selector for criteria that require it */}
            {needsQuiz && (
              <div className="mt-2 ml-2">
                <Label className="text-xs text-purple-700">Select Quiz:</Label>
                <select
                  value={c.quiz_id || ''}
                  onChange={(e) => updateCriteria(index, 'quiz_id', e.target.value || null)}
                  className="w-full p-2 border border-purple-300 rounded mt-1 text-sm"
                >
                  <option value="">-- Select a quiz --</option>
                  {quizzes.map(quiz => (
                    <option key={quiz.id} value={quiz.id}>
                      {quiz.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Description hint */}
            {criteriaType && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-2">
                {criteriaType.description}
              </p>
            )}
          </div>
        );
      })}
      <Button type="button" variant="outline" size="sm" onClick={addCriteria}>
        <Plus className="h-4 w-4 mr-1" />
        Add Criteria
      </Button>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        All criteria must be met to unlock the badge. Leave days empty for all-time.
      </p>
    </div>
  );
};

// ============================================
// Badge Editor Form
// ============================================
const BadgeEditor = ({ badge, onSave, onCancel, quizzes = [] }) => {
  const audioRef = useRef(null);
  const [formData, setFormData] = useState({
    name: badge?.name || '',
    description: badge?.description || '',
    icon_emoji: badge?.icon_emoji || '🏆',
    icon_url: badge?.icon_url || '',
    tts_text: badge?.tts_text || '',
    audio_url: badge?.audio_url || '',
    audio_source: badge?.audio_source || 'manual',
    category: badge?.category || 'milestone',
    criteria: badge?.criteria || [{ type: 'total_points', threshold: 100 }],
    points_reward: badge?.points_reward || 25,
    rarity: badge?.rarity || 'common',
    is_active: badge?.is_active ?? true,
    is_secret: badge?.is_secret ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('ja-JP-Neural2-D');
  const [speakingRate, setSpeakingRate] = useState(0.9);
  const [pitch, setPitch] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.url,
          audio_source: 'manual'
        });
        // Auto-play after upload
        if (audioRef.current) {
          audioRef.current.src = result.url;
          audioRef.current.load();
          audioRef.current.oncanplaythrough = () => {
            audioRef.current.play();
            audioRef.current.oncanplaythrough = null;
          };
        }
      } else {
        alert('Failed to upload audio file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload audio file');
    } finally {
      setUploading(false);
    }
  };

  const handleRecordingComplete = async (audioBlob) => {
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      const fileName = `recording_${Date.now()}.wav`;
      const file = new File([audioBlob], fileName, { type: audioBlob.type });
      uploadFormData.append('file', file);

      const response = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeaders()['Authorization']
        },
        body: uploadFormData
      });

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.url,
          audio_source: 'recording'
        });
        // Auto-play after recording
        if (audioRef.current) {
          audioRef.current.src = result.url;
          audioRef.current.load();
          audioRef.current.oncanplaythrough = () => {
            audioRef.current.play();
            audioRef.current.oncanplaythrough = null;
          };
        }
      } else {
        alert('Failed to upload recorded audio');
      }
    } catch (error) {
      console.error('Recording upload error:', error);
      alert('Failed to upload recorded audio');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateAudio = async () => {
    const textToSpeak = formData.tts_text || formData.name;
    if (!textToSpeak) {
      alert('Please enter a badge name or TTS text first');
      return;
    }

    if (!badge?.id) {
      alert('Please save the badge first before generating audio');
      return;
    }

    setGeneratingAudio(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/badges/${badge.id}/generate-audio`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            voice_name: selectedVoice,
            speaking_rate: speakingRate,
            pitch: pitch,
            text: textToSpeak
          })
        }
      );

      if (response.ok) {
        const result = await response.json();
        setFormData({
          ...formData,
          audio_url: result.audio_url,
          audio_source: 'google_tts'
        });
        // Auto-play after generation
        if (audioRef.current) {
          audioRef.current.src = result.audio_url;
          audioRef.current.load();
          audioRef.current.oncanplaythrough = () => {
            audioRef.current.play();
            audioRef.current.oncanplaythrough = null;
          };
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to generate audio');
      }
    } catch (error) {
      console.error('TTS error:', error);
      alert('Failed to generate audio');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const playAudio = () => {
    if (audioRef.current && formData.audio_url) {
      audioRef.current.play();
    }
  };

  const rarityConfig = BADGE_RARITIES.find(r => r.value === formData.rarity);

  return (
    <div className="bg-background rounded-lg shadow-sm border p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Preview Card */}
        <div className="flex justify-center mb-4">
          <div className={`p-4 rounded-xl border-2 ${rarityConfig?.color} text-center min-w-[160px]`}>
            <span className="text-4xl block mb-2">{formData.icon_emoji}</span>
            <p className="font-semibold">{formData.name || 'Badge Name'}</p>
            <p className="text-xs opacity-75">{rarityConfig?.label}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Name */}
          <div>
            <Label>Badge Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Vocabulary Champion"
              required
            />
          </div>

          {/* Emoji */}
          <div>
            <Label>Badge Emoji</Label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full p-2 border rounded-lg text-2xl text-left"
              >
                {formData.icon_emoji}
              </button>
              {showEmojiPicker && (
                <div className="absolute top-full left-0 mt-1 p-2 bg-background border rounded-lg shadow-lg z-10 grid grid-cols-5 gap-1">
                  {BADGE_EMOJIS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, icon_emoji: emoji });
                        setShowEmojiPicker(false);
                      }}
                      className="text-2xl p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <Label>Description</Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-2 border rounded-lg min-h-[80px]"
              placeholder="Describe what this badge is for..."
            />
          </div>

          {/* Category */}
          <div>
            <Label>Category</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-2 border rounded-lg"
            >
              {BADGE_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Rarity */}
          <div>
            <Label>Rarity</Label>
            <select
              value={formData.rarity}
              onChange={(e) => setFormData({ ...formData, rarity: e.target.value })}
              className="w-full p-2 border rounded-lg"
            >
              {BADGE_RARITIES.map(rarity => (
                <option key={rarity.value} value={rarity.value}>
                  {rarity.label}
                </option>
              ))}
            </select>
          </div>

          {/* Points Reward */}
          <div>
            <Label>Points Reward</Label>
            <Input
              type="number"
              min="0"
              value={formData.points_reward}
              onChange={(e) => setFormData({ ...formData, points_reward: parseInt(e.target.value) || 0 })}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Points awarded when badge is earned</p>
          </div>

          {/* Custom Icon Image (optional) */}
          <div className="md:col-span-2">
            <ImagePicker
              value={formData.icon_url}
              onChange={(url) => setFormData({ ...formData, icon_url: url })}
              context="badges"
              label="Custom Icon Image (optional)"
              previewSize="sm"
              allowClear={true}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upload a custom icon to use instead of emoji</p>
          </div>
        </div>

        {/* Audio Section */}
        <div className="border dark:border-purple-800 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/30">
          <Label className="font-medium flex items-center gap-2">
            <Volume2 className="h-4 w-4" />
            Badge Name Audio (Japanese Pronunciation)
          </Label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Add audio for the Japanese badge name pronunciation
          </p>

          {/* TTS Text Input */}
          <div className="mb-3">
            <Label className="text-sm">TTS Text (Japanese/Romanji to speak)</Label>
            <Input
              value={formData.tts_text}
              onChange={(e) => setFormData({ ...formData, tts_text: e.target.value })}
              placeholder="e.g., Hajimari (for 'Hajimari (始まり) - Beginning')"
              className="bg-background"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter just the Japanese word to speak. If empty, uses badge name.
            </p>
          </div>
          <audio ref={audioRef} src={formData.audio_url} />
          <div className="flex flex-wrap items-center gap-3">
            {formData.audio_url && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={playAudio}
                >
                  <Volume2 className="h-4 w-4 mr-2" />
                  Play Audio
                </Button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Source: {formData.audio_source === 'google_tts' ? 'Google TTS' : formData.audio_source === 'recording' ? 'Recording' : 'Manual Upload'}
                </span>
              </>
            )}

            <AudioRecorder
              onRecordingComplete={handleRecordingComplete}
              disabled={uploading}
            />

            <label className="cursor-pointer">
              <input
                type="file"
                accept="audio/*"
                onChange={handleAudioUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                asChild
              >
                <span>
                  {uploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload Audio
                </span>
              </Button>
            </label>

            {badge?.id && (
              <div className="w-full mt-3 space-y-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    value={selectedVoice}
                    onChange={(e) => setSelectedVoice(e.target.value)}
                    className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                  >
                    {TTS_VOICES.map(voice => (
                      <option key={voice.name} value={voice.name}>
                        {voice.gender} ({voice.type})
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateAudio}
                    disabled={generatingAudio || (!formData.tts_text && !formData.name)}
                  >
                    {generatingAudio ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4 mr-2" />
                    )}
                    Generate with TTS
                  </Button>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <label className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Speed:</label>
                    <input
                      type="range"
                      min="0.5"
                      max="1.5"
                      step="0.1"
                      value={speakingRate}
                      onChange={(e) => setSpeakingRate(parseFloat(e.target.value))}
                      className="w-20"
                    />
                    <span className="text-gray-500 dark:text-gray-400 w-8">{speakingRate}x</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Pitch:</label>
                    <input
                      type="range"
                      min="-10"
                      max="10"
                      step="1"
                      value={pitch}
                      onChange={(e) => setPitch(parseFloat(e.target.value))}
                      className="w-20"
                    />
                    <span className="text-gray-500 dark:text-gray-400 w-8">{pitch > 0 ? '+' : ''}{pitch}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Criteria */}
        <CriteriaBuilder
          criteria={formData.criteria}
          onChange={(criteria) => setFormData({ ...formData, criteria })}
          quizzes={quizzes}
        />

        {/* Toggles */}
        <div className="flex flex-wrap gap-4 border-t pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Active</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_secret}
              onChange={(e) => setFormData({ ...formData, is_secret: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm flex items-center gap-1">
              {formData.is_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              Secret Badge
            </span>
          </label>
        </div>
        {formData.is_secret && (
          <p className="text-xs text-purple-600">
            Secret badges are hidden until earned - students won't know they exist!
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="adminPrimary"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {badge?.id ? 'Update Badge' : 'Create Badge'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ============================================
// Main Badge Manager Component
// ============================================
const BadgeManager = () => {
  const [badges, setBadges] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBadge, setEditingBadge] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const audioRef = useRef(null);

  const playBadgeAudio = (badge) => {
    if (badge.audio_url && audioRef.current) {
      audioRef.current.src = badge.audio_url;
      audioRef.current.play();
    }
  };

  const fetchBadges = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/badges`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setBadges(data);
      }
    } catch (error) {
      console.error('Failed to fetch badges:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuizzes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/quizzes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setQuizzes(data);
      }
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    }
  }, []);

  useEffect(() => {
    fetchBadges();
    fetchQuizzes();
  }, [fetchBadges, fetchQuizzes]);

  const handleSave = async (badgeData) => {
    try {
      const isNew = !editingBadge?.id;
      const url = isNew
        ? `${API_BASE}/api/admin/badges`
        : `${API_BASE}/api/admin/badges/${editingBadge.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(badgeData)
      });

      if (response.ok) {
        await fetchBadges();
        setEditingBadge(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save badge');
      }
    } catch (error) {
      console.error('Failed to save badge:', error);
      alert('Failed to save badge');
    }
  };

  const handleDelete = async (badgeId) => {
    if (!window.confirm('Are you sure you want to delete this badge? Students who earned it will lose it.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/badges/${badgeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchBadges();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete badge');
      }
    } catch (error) {
      console.error('Failed to delete badge:', error);
    }
  };

  const filteredBadges = badges.filter(badge => {
    const matchesSearch = badge.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      badge.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || badge.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (editingBadge !== null) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <button
            onClick={() => setEditingBadge(null)}
            className="hover:text-purple-600"
          >
            Badges
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-gray-100">
            {editingBadge?.id ? 'Edit Badge' : 'New Badge'}
          </span>
        </div>
        <BadgeEditor
          badge={editingBadge}
          onSave={handleSave}
          onCancel={() => setEditingBadge(null)}
          quizzes={quizzes}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden audio element for playback */}
      <audio ref={audioRef} />

      <PageTitle
        title="Badge Manager"
        description="Create and manage achievement badges"
        icon={<Medal className="h-6 w-6" />}
        actions={
          <Button
            onClick={() => setEditingBadge({})}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Badge
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Input
            type="text"
            placeholder="Search badges..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="p-2 border rounded-lg"
        >
          <option value="">All Categories</option>
          {BADGE_CATEGORIES.map(cat => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      {/* Badge Grid */}
      {filteredBadges.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed dark:border-gray-700">
          <Medal className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery || categoryFilter ? 'No badges found' : 'No badges yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery || categoryFilter
              ? 'Try adjusting your filters'
              : 'Create your first badge to motivate students'}
          </p>
          {!searchQuery && !categoryFilter && (
            <Button
              onClick={() => setEditingBadge({})}
              variant="adminPrimary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Badge
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBadges.map(badge => {
            const rarityConfig = BADGE_RARITIES.find(r => r.value === badge.rarity);
            return (
              <Card
                key={badge.id}
                className={`hover:shadow-md transition-all ${!badge.is_active ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-3 rounded-xl border-2 ${rarityConfig?.color}`}>
                      <span className="text-3xl">{badge.icon_emoji}</span>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{badge.name}</h3>
                        {badge.is_secret && (
                          <EyeOff className="h-4 w-4 text-purple-500" title="Secret Badge" />
                        )}
                        {!badge.is_active && (
                          <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                            Inactive
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {badge.description}
                      </p>

                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${rarityConfig?.color}`}>
                          {rarityConfig?.label}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                          {BADGE_CATEGORIES.find(c => c.value === badge.category)?.label}
                        </span>
                        <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded-full">
                          +{badge.points_reward} pts
                        </span>
                      </div>

                      {badge.earned_count > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Earned by {badge.earned_count} student(s)
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-1">
                      {badge.audio_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => playBadgeAudio(badge)}
                          title="Play audio"
                        >
                          <Volume2 className="h-4 w-4 text-purple-600" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingBadge(badge)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(badge.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BadgeManager;
